'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

/** '오늘 하루 안보기' 를 누른 사용자의 숨김 만료 시각(다음 로컬 자정, epoch ms 문자열). localStorage 에 둔다. */
export const AUTO_VIEWING_END_NOTICE_HIDDEN_UNTIL_KEY = 'autoViewingEndNotice-hiddenUntil';

/** '공지사항 바로가기' 이동처. 확정된 오토뷰업 종료 공지 상세. */
export const AUTO_VIEWING_END_NOTICE_URL = '/notice/01a1a5b4-426b-706a-b472-b5e22caf38a8';

/**
 * 웹 오토뷰업 종료 안내. 플래그와 무관하게 홈 마운트 즉시 열고, '오늘 하루 안보기' 기록이 유효할 때만 열지 않는다.
 * - isChecked=false : 아직 모름(마운트 직후 첫 렌더). 호출부는 뒤 팝업을 열거나 마운트하지 않는다.
 * - isChecked=true && isOpen=false : 닫힘(판정 끝).
 */
export const useAutoViewingEndNotice = (): {
  isOpen: boolean;
  isChecked: boolean;
  close: () => void;
  hideToday: () => void;
  goNotice: () => void;
} => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  // 마운트 시 한 번만 판정한다. localStorage 는 클라이언트에서만 읽을 수 있어 effect 에서 본다.
  useEffect(() => {
    setIsChecked(true);

    try {
      const hiddenUntil = Number(
        window.localStorage.getItem(AUTO_VIEWING_END_NOTICE_HIDDEN_UNTIL_KEY),
      );

      // 값이 없거나(null → 0) 숫자가 아니면 숨김 기록이 없는 것으로 본다. 만료 시각 정각부터는 다시 연다.
      if (Number.isFinite(hiddenUntil) && Date.now() < hiddenUntil) return;
    } catch {
      // storage 접근이 throw 할 수 있다 — 읽지 못하면 연다.
    }

    setIsOpen(true);
  }, []);

  // X — 이번 화면에서만 닫는다. 저장하지 않으므로 새로고침하면 다시 뜬다.
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // 오늘 하루 안보기 — 닫고 다음 로컬 자정까지 숨긴다.
  const hideToday = useCallback(() => {
    setIsOpen(false);

    try {
      const now = new Date(Date.now());
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

      window.localStorage.setItem(
        AUTO_VIEWING_END_NOTICE_HIDDEN_UNTIL_KEY,
        String(nextMidnight.getTime()),
      );
    } catch {
      // 저장에 실패해도 팝업은 닫는다 — 새로고침 시 다시 뜰 뿐이다.
    }
  }, []);

  // 공지로 이동 — 팝업은 내리지 않는다(페이지 이탈 시 언마운트). 내리면 이동 중 화면에 리뉴얼 모달이
  // 이어서 뜬다. 숨김 기록도 남기지 않는다(돌아오면 다시 볼 수 있다).
  const goNotice = useCallback(() => {
    router.push(AUTO_VIEWING_END_NOTICE_URL);
  }, [router]);

  return { close, goNotice, hideToday, isChecked, isOpen };
};
